/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { whitespace } from "stylis";
import { enUS } from "date-fns/locale";
import { useSystemTheme } from "context/SystemThemeContext";

function MiniStatisticsCard({ 
  bgColor = "white", 
  title = { fontWeight: "medium", text: "" }, 
  count, 
  count1, 
  percentage = { color: "success", text: "" }, 
  icon, 
  direction = "right" 
}) {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const FormattedNumber = ({ value }) => {
    const formatted = new Intl.NumberFormat(enUS).format(value);
    return <span>{formatted}</span>;
  };
  
  // Determine card background color based on theme
  const getCardBgColor = () => {
    if (bgColor === "white") {
      return isDarkMode ? "#060C18" : "#ffffff";
    }
    return bgColor;
  };
  
  // Determine text color based on theme
  const getTextColor = (defaultColor) => {
    if (bgColor === "white" && isDarkMode) {
      return "#e2e8f0";
    }
    return defaultColor;
  };
  
  // Determine icon box background color
  const getIconBoxBg = () => {
    if (bgColor === "white") {
      return isDarkMode ? "#2d2d3f" : icon.color;
    }
    return "white";
  };
  
  // Determine icon color
  const getIconColor = () => {
    if (bgColor === "white") {
      return isDarkMode ? "#000000" : "white";
    }
    return "dark";
  };
  
  return (
    <Card sx={{ 
      height: "100%", 
      display: "flex", 
      flexDirection: "column",
      backgroundColor: getCardBgColor(),
      transition: "background-color 0.3s ease",
      border: isDarkMode && bgColor === "white" ? "1px solid #2d2d3f" : "none",
    }}>
      <SoftBox 
        bgColor={bgColor} 
        variant="gradient"  
        sx={{
          flexGrow: 1, 
          display: "flex", 
          alignItems: "center",
          backgroundColor: getCardBgColor(),
        }}
      >
        <SoftBox p={2} sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
          <Grid container alignItems="center">
            {direction === "left" ? (
              <Grid item>
                <SoftBox
                  variant="gradient"
                  bgColor={getIconBoxBg()}
                  color={getIconColor()}
                  width="3rem"
                  height="3rem"
                  borderRadius="md"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  shadow="md"
                >
                  <Icon fontSize="small" color="inherit">
                    {icon.component}
                  </Icon>
                </SoftBox>
              </Grid>
            ) : null}
            
            <Grid item xs={8}>
              <SoftBox ml={direction === "left" ? 2 : 0} lineHeight={1}>
                <SoftTypography
                  variant="button"
                  color={bgColor === "white" ? "text" : "white"}
                  opacity={bgColor === "white" ? 1 : 0.7}
                  textTransform="capitalize"
                  fontWeight={title.fontWeight}
                  sx={{
                    whitespace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                    color: getTextColor(undefined),
                  }}
                >
                  {title.text}
                </SoftTypography>
                <SoftTypography
                  variant="h5"
                  fontWeight="bold"
                  color={bgColor === "white" ? "dark" : "white"}
                  sx={{
                    color: getTextColor(bgColor === "white" ? "#1a202c" : undefined),
                  }}
                >
                  {count1 ? (
                    <span>{count1}</span>
                  ) : count ? (
                    <FormattedNumber value={count} />
                  ) : 0}
                  
                  <SoftTypography 
                    variant="button" 
                    color={percentage.color} 
                    fontWeight="bold"
                    sx={{
                      color: percentage.color === "success" 
                        ? "#43A047" 
                        : percentage.color === "error" 
                        ? "#E53935" 
                        : percentage.color === "warning" 
                        ? "#FB8C00" 
                        : percentage.color,
                    }}
                  >
                    {percentage.text}
                  </SoftTypography>
                </SoftTypography>
              </SoftBox>
            </Grid>
           
            {direction === "right" ? (
              <Grid item xs={4}>
                <SoftBox
                  variant="gradient"
                  bgColor={getIconBoxBg()}
                  color={getIconColor()}
                  width="3rem"
                  height="3rem"
                  marginLeft="auto"
                  borderRadius="md"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  shadow="md"
                >
                  <Icon fontSize="small" color="inherit">
                    {icon.component}
                  </Icon>
                </SoftBox>
              </Grid>
            ) : null}
          </Grid>
        </SoftBox>
      </SoftBox>
    </Card>
  );
}

// Typechecking props for the MiniStatisticsCard
MiniStatisticsCard.propTypes = {
  bgColor: PropTypes.oneOf([
    "white",
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  title: PropTypes.PropTypes.shape({
    fontWeight: PropTypes.oneOf(["light", "regular", "medium", "bold"]),
    text: PropTypes.string,
  }),
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  count1: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  percentage: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "white",
    ]),
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  icon: PropTypes.shape({
    color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
    component: PropTypes.node.isRequired,
  }).isRequired,
  direction: PropTypes.oneOf(["right", "left"]),
};

export default MiniStatisticsCard;